import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, Popconfirm, Space, Table, Tag, message } from "antd";
import { CheckCircleOutlined, ReloadOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { createLmsApi, getLmsErrorMessage, getLmsListItems } from "../../lms/api";
import { statusColor } from "../../lms/constants";
import { LmsErrorAlert, LmsPageHeader, formatDateTime } from "../../lms/components/LmsPrimitives";

const LmsReports = () => {
  const navigate = useNavigate();
  const api = useMemo(() => createLmsApi(navigate), [navigate]);
  const [reports, setReports] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadReports = useCallback(async (page = 1, pageSize = 20) => {
    setLoading(true);
    const response = await api.weeklyReports.list({ page, pageSize, ordering: "-generated_at" });
    if (response?.ok) {
      const result = getLmsListItems(response.data);
      setReports(result.items);
      setPagination({ current: page, pageSize, total: result.count });
      setError("");
    } else setError(getLmsErrorMessage(response, "Unable to load weekly reports"));
    setLoading(false);
  }, [api]);

  useEffect(() => {
    loadReports(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadReports]);

  const runAction = async (action, record, successMessage) => {
    const response = await action(record.id);
    if (!response?.ok) {
      message.error(getLmsErrorMessage(response, "Unable to update report"));
      return;
    }
    message.success(successMessage);
    loadReports(1, pagination.pageSize);
  };

  const columns = [
    { title: "Session", dataIndex: "sessionTitle", key: "sessionTitle" },
    { title: "Week", dataIndex: "weekNumber", key: "weekNumber", render: (value) => `Week ${value}` },
    { title: "Teachers", key: "teachers", render: (_, record) => `${record.activeTeachersCount || 0} active · ${record.inactiveTeachersCount || 0} inactive` },
    { title: "Learners", dataIndex: "activeLearnersCount", key: "activeLearnersCount" },
    { title: "Uploads", dataIndex: "totalContentUploads", key: "totalContentUploads" },
    { title: "Live sessions", dataIndex: "totalLiveSessions", key: "totalLiveSessions" },
    { title: "Engagement", dataIndex: "avgLearnerEngagementPct", key: "avgLearnerEngagementPct", render: (value) => `${value || 0}%` },
    { title: "Status", dataIndex: "status", key: "status", render: (value) => <Tag color={statusColor(value)}>{value}</Tag> },
    { title: "Generated", dataIndex: "generatedAt", key: "generatedAt", render: (value) => formatDateTime(value) },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          {record.status === "Draft" && <Popconfirm title="Mark this report as reviewed?" onConfirm={() => runAction(api.weeklyReports.review, record, "Report marked as reviewed")}><Button icon={<SafetyCertificateOutlined />}>Review</Button></Popconfirm>}
          {record.status === "Deputy Reviewed" && <Popconfirm title="Approve this weekly report?" onConfirm={() => runAction(api.weeklyReports.approve, record, "Report approved")}><Button type="primary" icon={<CheckCircleOutlined />}>Approve</Button></Popconfirm>}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <LmsPageHeader title="LMS Weekly Stats" description="Reports are generated automatically each week. Review and approve them here." extra={<Button icon={<ReloadOutlined />} loading={loading} onClick={() => loadReports(1, pagination.pageSize)}>Refresh</Button>} />
      <LmsErrorAlert error={error} onClose={() => setError("")} />
      <Card><Table rowKey="id" columns={columns} dataSource={reports} loading={loading} scroll={{ x: 1250 }} pagination={{ ...pagination, showSizeChanger: false }} onChange={(nextPagination) => loadReports(nextPagination.current, nextPagination.pageSize)} /></Card>

    </div>
  );
};

export default LmsReports;
